import { InfoIcon, Typography, WithLoader } from '@analytical-alley/ui';
import React, { useMemo } from 'react';

import { ComparisonChartCard } from '@/dashboards/ExecutiveSummary/components/ComparisonChartCard';
import { useExecutiveSummaryContext } from '@/dashboards/ExecutiveSummary/context';
import { formatRangeLabel } from '@/dashboards/ExecutiveSummary/helpers/formatRangeLabel';
import {
    useCompetitorContributionProductQuery,
    useCompetitorImpactQuery,
    useCompetitorSpendQuery,
} from '@/dashboards/ExecutiveSummary/hooks';
import { formatMissingVariablesNotice } from '@/dashboards/CompetitorsMediaInvestment/helpers/formatMissingVariablesNotice';
import { useConfigContext } from '@/context/configContext';
import { getROIConfig } from '@/dashboards/SpendSummary/utils';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';

export const CompetitorImpactCharts = () => {
    const {
        data: impact,
        isLoading: isImpactLoading,
        isFetching: isImpactFetching,
    } = useCompetitorImpactQuery();
    const {
        data: spend,
        isLoading: isSpendLoading,
        isFetching: isSpendFetching,
        missingVariables: spendMissingVariables,
    } = useCompetitorSpendQuery();
    const {
        data: contributionProduct,
        isLoading: isContributionProductLoading,
        isFetching: isContributionProductFetching,
    } = useCompetitorContributionProductQuery();

    const {
        project: { roiType },
        contributionFormatter,
    } = useDashboardGlobalContext();
    const { formatNumber } = useConfigContext();
    const { primaryRange, comparisonRange } = useExecutiveSummaryContext();
    const { name, calculate, description } = useMemo(
        () => getROIConfig(roiType),
        [roiType],
    );
    const spendNotice = useMemo(
        () => formatMissingVariablesNotice(spendMissingVariables),
        [spendMissingVariables],
    );

    const primaryLabel = formatRangeLabel(primaryRange);
    const comparisonLabel = comparisonRange
        ? formatRangeLabel(comparisonRange)
        : undefined;

    const primaryTotalRoi = calculate({
        spend: spend.primary,
        contribution: impact.primary,
        contributionCostProduct: contributionProduct.primary,
    });
    const comparisonTotalRoi = calculate({
        spend: spend.comparison,
        contribution: impact.comparison,
        contributionCostProduct: contributionProduct.comparison,
    });

    return (
        <div className="glass-tile flex flex-col gap-2 px-8 py-6 max-lg:px-5 max-lg:max-w-full">
            <Typography variant="titleS">Competitor Impact</Typography>
            <WithLoader
                isLoading={
                    isImpactLoading ||
                    isSpendLoading ||
                    isContributionProductLoading
                }
                isFetching={
                    isImpactFetching ||
                    isSpendFetching ||
                    isContributionProductFetching
                }
            >
                <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-3">
                    <ComparisonChartCard
                        title="Competitor Impact"
                        axisLabel="Competitor Impact"
                        primary={impact.primary}
                        comparison={impact.comparison}
                        formatValue={contributionFormatter}
                        primaryLabel={primaryLabel}
                        comparisonLabel={comparisonLabel}
                    />
                    <ComparisonChartCard
                        title={
                            spendNotice ? (
                                <span className="flex justify-center gap-1 font-medium">
                                    Competitor Media Spend
                                    <InfoIcon>{spendNotice}</InfoIcon>
                                </span>
                            ) : (
                                'Competitor Media Spend'
                            )
                        }
                        axisLabel="Competitor Spend"
                        primary={spend.primary}
                        comparison={spend.comparison}
                        primaryLabel={primaryLabel}
                        comparisonLabel={comparisonLabel}
                    />
                    <ComparisonChartCard
                        title={
                            <span className="flex justify-center gap-1 font-medium">
                                Total Competitor {name}
                                <InfoIcon>{description}</InfoIcon>
                            </span>
                        }
                        axisLabel={`Competitor ${name}`}
                        primary={primaryTotalRoi}
                        comparison={comparisonTotalRoi}
                        formatValue={(v) =>
                            roiType === 'COST_PER_UNIT'
                                ? formatNumber(v, {
                                      style: 'currency',
                                      maximumFractionDigits: 2,
                                      minimumFractionDigits: 2,
                                  })
                                : v.toFixed(2)
                        }
                        primaryLabel={primaryLabel}
                        comparisonLabel={comparisonLabel}
                    />
                </div>
            </WithLoader>
        </div>
    );
};
